/// <reference types="vite-plugin-svgr/client" />
import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Routes, Route, useParams } from 'react-router';
import Feedback from './components/common/Feedback';
import Loader from './components/common/Loader';
import PrivateRoute from './components/auth/PrivateRoute';
import Toast from './components/common/Toast';
import theme from './theme';
import { AUTH_CONFIG } from './auth0-config';
import { AppContextProvider } from './context/AppContextProvider';
import { IDocument, IExtraction, IOrg } from './types/my-types';
import { IFeedback, IToast, TSetExtractions, TSetToast } from './types/util';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { Provider as ProviderJotai, useAtom } from 'jotai';
import TourProvider from './components/common/Tour/TourProvider';
import { favoriteOrgsAtom } from './atoms/org-atoms';
import { loadFavoriteOrgs, saveFavoriteOrgs, clearSessionStorage } from './utils/localStorageUtils';

import DocumentsPage from './pages/DocumentsPage';
import DocumentDetailsPage from './pages/DocumentDetailsPage';
import DocumentSplitPage from './pages/DocumentSplitPage';
import ExtractionBatchListPage from './pages/ExtractionBatchListPage';
import TrainingPage from './pages/TrainingPage';
import FieldPage from './pages/FieldPage';
import MarkPage from './pages/MarkPage';
import ReviewPage from './pages/ReviewPage';
import ReviewGroupPage from './pages/ReviewGroupPage';
import OrgPage from './pages/OrgPage';
import MATPage from './pages/MATPage';
import MATDiscoveryPage from './pages/MATDiscoveryPage';
import SelectDefaultsPage from './pages/SelectDefaultsPage';
import Page404 from './pages/Page404';
import LogoutPage from './pages/LogoutPage';
import DataExportsPage from './pages/DataExportsPage';
import ExtractionBatchDetailsGroup from './components/extractionBatches/details/ExtractionBatchDetailsGroup';
import ExtractionBatchDetailsPage from './pages/ExtractionBatchDetailsPage';
import TrainingSetDetailsPage from './pages/TrainingSetDetailsPage';
import PackagePage from './pages/PackagerPage';
import TransformedDataPage from './pages/TransformedDataPage';
import VendorsPage from './pages/VendorsPage';

import ReviewPageWithoutExtractionId from './pages/ReviewPageWithoutExtractionId';
import ReviewPageByDocId from './pages/ReviewPageByDocId';
import RulesetsListPage from './pages/RulesetsListPage';

const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

// TODO: move interface to context folder

export interface IAppContext {
  accessibility: boolean;
  setAccessibility: (value: boolean) => void;
  orgLabel: string;
  setOrgLabel: (value: string) => void;
  orgId: string;
  setOrgId: (value: string) => void;
  dataPoolId: string;
  setDataPoolId: (value: string) => void;
  dataPoolLabel: string;
  setDataPoolLabel: (value: string) => void;
  showReviewSpreadsheet: boolean;
  setShowReviewSpreadsheet: (value: boolean) => void;
  dataPoolTotal: number;
  setDataPoolTotal: (value: string) => void;
  feedback: IFeedback;
  setFeedback: (value: IFeedback) => void;
  toast: IToast;
  setToast: (value: TSetToast) => void;
  isStaff: boolean;
  setIsStaff: (value: boolean) => void;
  docs: IDocument[] | undefined;
  setDocs: (value: IDocument[]) => void;
  extractions: IExtraction[] | undefined;
  setExtractions: (value: TSetExtractions) => void;
  ids: string[];
  setIds: (value: string[]) => void;
  extractionIndex: number;
  setExtractionIndex: (value: number) => void;
  setMaintenance: (value: boolean) => void;
  maintenance: boolean;
  org?: IOrg;
  setOrg: (org: IOrg) => void;
}

export const AppContext = React.createContext({} as IAppContext);

let MINUTES_UNTIL_AUTO_LOGOUT = 30; // in mins
let CHECK_INTERVAL = 30000; // in ms
const STORE_KEY = 'lastAction';

function App() {
  const { isAuthenticated, user, logout } = useAuth0();
  const [showDevtools, setShowDevtools] = React.useState(false);

  React.useEffect(() => {
    // @ts-ignore
    window.toggleDevtools = () => setShowDevtools((old) => !old);

    const shortTimeout = localStorage.getItem('shortTimeout');
    if (shortTimeout) {
      MINUTES_UNTIL_AUTO_LOGOUT = 0.05;
      CHECK_INTERVAL = 2000;
    }
  }, []);

  useEffect(() => {
    if (user) {
      // console.log({ user });
      // @ts-ignore
      window.userpilot.identify(
        // @ts-ignore
        user.email + user.nickname, // Used to identify users
        {
          name: user.nickname, // Full name
          email: user.email, // Email address
          created_at: user.updated_at, // Signup date as a Unix timestamp
        }
      );
    }
  }, [user]);

  function getLastAction(): number {
    const lastAction = localStorage.getItem(STORE_KEY);
    return lastAction ? parseInt(lastAction) : 0;
  }

  function setLastAction(lastAction: number) {
    localStorage.setItem(STORE_KEY, lastAction.toString());
  }

  function initListener() {
    document.body.addEventListener('click', () => reset());
    document.body.addEventListener('mouseover', () => reset());
    document.body.addEventListener('mouseout', () => reset());
    document.body.addEventListener('keydown', () => reset());
    document.body.addEventListener('keyup', () => reset());
    document.body.addEventListener('keypress', () => reset());
  }

  function reset() {
    setLastAction(Date.now());
  }

  function initInterval() {
    setInterval(() => {
      check();
    }, CHECK_INTERVAL);
  }

  function check() {
    const now = Date.now();
    const lastAction = getLastAction();
    const timeleft = lastAction + MINUTES_UNTIL_AUTO_LOGOUT * 60 * 1000;
    const diff = timeleft - now;
    const isTimeout = diff < 0;
    if (isTimeout && isAuthenticated && localStorage.getItem('uploading') !== 'true') {
      clearSessionStorage();
      logout({ logoutParams: { returnTo: `${AUTH_CONFIG.hostUrl}/logout` } });
      // window.location.href = `https://${AUTH_CONFIG.domain}/v2/logout?returnTo=${AUTH_CONFIG.hostUrl}/logout`;
    }
  }

  initListener();
  initInterval();

  const queryClient = new QueryClient();

  if (import.meta.env.VITE_APP_ENV === 'production') {
    Sentry.init({
      dsn: 'https://36d7d1778b7895b470a23cf47b32d15e@o4509244679192576.ingest.us.sentry.io/4509244682469376',
      sendDefaultPii: false,
    });
  }

  return (
    <Suspense fallback={<Loader />}>
      <ProviderJotai>
        <FavoriteOrgsSync />
        <AppContextProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <QueryClientProvider client={queryClient}>
                <TourProvider>
                  <BrowserRouter>
                    <Routes>
                      <Route element={<PrivateRoute isAllowed={isAuthenticated} />}>
                        {/* FIXME: Need to resolve non staff selectDefaultsPage routes */}
                        <Route path="/" element={<ExtractionBatchListPage />} />
                        <Route path="/vendors" element={<VendorsPage />} />

                        <Route
                          path="/data-pools/:dpid/data-exports"
                          element={<DataExportsPage />}
                        />
                        <Route path="/organization/" element={<OrgPage />} />
                        <Route path="/organization/:orgId" element={<OrgPage />} />
                        <Route path="/data-pools/:dpid/mat" element={<MATPage />} />
                        <Route
                          path="/data-pools/:dpid/mat-discovery"
                          element={<MATDiscoveryPage />}
                        />
                        <Route path="/data-pools/:dpid/documents" element={<DocumentsPage />} />
                        <Route
                          path="/data-pools/:dpid/documents/:documentId"
                          element={<DocumentDetailsPage />}
                        />
                        <Route
                          path="/data-pools/:dpid/documents/:documentId/split"
                          element={<DocumentSplitPage />}
                        />

                        <Route path="/data-pools/:dpid/train" element={<TrainingPage />} />
                        <Route
                          path="/data-pools/:dpid/train/:id/details"
                          element={<TrainingSetDetailsPage />}
                        />
                        <Route path="/data-pools/:dpid/train/:id/fields" element={<FieldPage />} />
                        <Route
                          path="/data-pools/:dpid/train/:id/mark/:did"
                          element={<MarkPage />}
                        />
                        {/* Legacy redirects for training set routes */}
                        <Route
                          path="/data-pools/:dpid/train/:id"
                          element={<LegacyTrainDetailsRedirect />}
                        />
                        <Route
                          path="/data-pools/:dpid/fields/:id"
                          element={<LegacyFieldsRedirect />}
                        />
                        <Route
                          path="/data-pools/:dpid/manage/:id"
                          element={<LegacyManageRedirect />}
                        />
                        <Route path="data-pools/:dpid/packager/" element={<PackagePage />} />
                        <Route path="/data-pools/:dpid/rulesets" element={<RulesetsListPage />} />
                        <Route
                          path="data-pools/:dpid/extraction-batches/"
                          element={<ExtractionBatchListPage />}
                        />

                        <Route
                          path="/data-pools/:dpid/extraction-batch/:id/details"
                          element={<ExtractionBatchDetailsPage />}
                        />
                        <Route
                          path="/data-pools/:dpid/extraction-batch/:id/transformed"
                          element={<TransformedDataPage />}
                        />
                        <Route
                          path="/data-pools/:dpid/extraction-batch/:id/review/:eid"
                          element={<ReviewPage />}
                        />
                        <Route
                          path="/data-pools/:dpid/extraction-batch/:id/review/document/:documentId"
                          element={<ReviewPageByDocId />}
                        />
                        <Route
                          path="/data-pools/:dpid/extraction-batch-details/:id/group/:gid"
                          element={<ExtractionBatchDetailsGroup />}
                        />

                        <Route
                          path="/data-pools/:dpid/extraction-batch-details/:id"
                          element={<LegacyDetailsRedirect />}
                        />

                        <Route
                          path="/data-pools/:dpid/extraction-batch-transformed/:extractionBatchId"
                          element={<LegacyTransformedRedirect />}
                        />

                        <Route
                          path="/data-pools/:dpid/mark/:id/document/:did"
                          element={<LegacyMarkRedirect />}
                        />
                        <Route
                          path="/data-pools/:dpid/review/:id/extraction/"
                          element={<ReviewPageWithoutExtractionId />}
                        />
                        <Route
                          path="/data-pools/:dpid/review/:id/extraction/:eid"
                          element={<LegacyReviewRedirect />}
                        />
                        <Route
                          path="/data-pools/:dpid/review/:id/document/:documentId"
                          element={<LegacyReviewByDocIdRedirect />}
                        />
                        <Route
                          path="/data-pools/:dpid/review/:id/group/:tid/extraction/:eid"
                          element={<ReviewGroupPage />}
                        />

                        <Route path="/selectDefault/" element={<SelectDefaultsPage />} />
                      </Route>

                      <Route path="/logout" element={<LogoutPage />} />

                      <Route element={<Page404 />} />
                    </Routes>
                  </BrowserRouter>
                </TourProvider>
                <Toast />
                <Feedback />
                <ReactQueryDevtools initialIsOpen={false} />
                {showDevtools && import.meta.env.NODE_ENV !== 'production' && (
                  <React.Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction />
                  </React.Suspense>
                )}
              </QueryClientProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </AppContextProvider>
      </ProviderJotai>
    </Suspense>
  );
}

/** Syncs the favoriteOrgs Jotai atom with per-user/env localStorage. */
function FavoriteOrgsSync() {
  const { user } = useAuth0();
  const [favorites, setFavorites] = useAtom(favoriteOrgsAtom);
  const initializedRef = useRef(false);
  const skipNextSaveRef = useRef(false);
  const userEmailRef = useRef<string | undefined>(undefined);

  // Load from localStorage when user becomes available
  useEffect(() => {
    if (user?.email) {
      const stored = loadFavoriteOrgs(user.email);
      skipNextSaveRef.current = true;
      setFavorites(stored);
      initializedRef.current = true;
      userEmailRef.current = user.email;
    } else {
      initializedRef.current = false;
      userEmailRef.current = undefined;
    }
  }, [user?.email, setFavorites]);

  // Save to localStorage when favorites change
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    if (initializedRef.current && userEmailRef.current) {
      saveFavoriteOrgs(userEmailRef.current, favorites);
    }
  }, [favorites]);

  return null;
}

export default App;

// --- Legacy Redirect Helpers for route migration ---
function LegacyDetailsRedirect() {
  const { dpid, id } = useParams();
  return <Navigate to={`/data-pools/${dpid}/extraction-batch/${id}/details`} replace />;
}

function LegacyTransformedRedirect() {
  const { dpid, extractionBatchId } = useParams();
  return (
    <Navigate
      to={`/data-pools/${dpid}/extraction-batch/${extractionBatchId}/transformed`}
      replace
    />
  );
}

function LegacyReviewRedirect() {
  const { dpid, id, eid } = useParams();
  return <Navigate to={`/data-pools/${dpid}/extraction-batch/${id}/review/${eid}`} replace />;
}

function LegacyReviewByDocIdRedirect() {
  const { dpid, id, documentId } = useParams();
  return (
    <Navigate
      to={`/data-pools/${dpid}/extraction-batch/${id}/review/document/${documentId}`}
      replace
    />
  );
}

function LegacyTrainDetailsRedirect() {
  const { dpid, id } = useParams();
  return <Navigate to={`/data-pools/${dpid}/train/${id}/details`} replace />;
}

function LegacyManageRedirect() {
  const { dpid, id } = useParams();
  return <Navigate to={`/data-pools/${dpid}/train/${id}/details`} replace />;
}

function LegacyFieldsRedirect() {
  const { dpid, id } = useParams();
  return <Navigate to={`/data-pools/${dpid}/train/${id}/fields`} replace />;
}

function LegacyMarkRedirect() {
  const { dpid, id, did } = useParams();
  return <Navigate to={`/data-pools/${dpid}/train/${id}/mark/${did}`} replace />;
}
